import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ImagePlus, Loader2, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { resolveImageUrl } from "@/lib/images";

// Per-door illustration: tiny thumbnail button (upload / replace / remove).
export default function DoorImageButton({ image, index, onChange }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  async function upload(file) {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/game-background", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(r.data.url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <div className="relative shrink-0">
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid={`advent-door-image-input-${index + 1}`}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        title={t("game_editor.advent_door_image", "Image de la case (facultative)")}
        className={`h-8 w-8 overflow-hidden rounded-lg border transition-colors ${
          image ? "border-border" : "border-dashed border-border/70 text-muted-foreground hover:border-foreground/40 hover:text-foreground"
        } flex items-center justify-center`}
        data-testid={`advent-door-image-btn-${index + 1}`}
      >
        {uploading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : image ? (
          <img src={resolveImageUrl(image)} alt="" className="h-full w-full object-cover" />
        ) : (
          <ImagePlus className="h-3.5 w-3.5" />
        )}
      </button>
      {image && !uploading && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="absolute -right-1.5 -top-1.5 h-4 w-4 rounded-full bg-foreground text-background flex items-center justify-center shadow"
          aria-label={t("common.delete", "Supprimer")}
          data-testid={`advent-door-image-remove-${index + 1}`}
        >
          <X className="h-2.5 w-2.5" />
        </button>
      )}
    </div>
  );
}
